/**
 * cleanup-helpers/callable-actions.ts
 *
 * Wrappers around Cloud Functions that mutate data. Each function is exposed
 * to the browser console (via cleanup-helper barrel) and logs progress for
 * operators running maintenance from the production console.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import type { CleanupResult, ReclassifyResult } from './types';

/**
 * Cleanup old transactions without transactionHash
 */
export async function cleanupOldTransactions(
  dryRun: boolean = true,
  confirm: boolean = false
): Promise<CleanupResult> {
  console.log('[cleanupHelper] Starting cleanup:', { dryRun, confirm });

  const cleanupFn = httpsCallable<{ dryRun: boolean; confirm: boolean }, CleanupResult>(
    functions,
    'cleanupOldTransactions'
  );

  try {
    const result = await cleanupFn({ dryRun, confirm });
    console.log('[cleanupHelper] Result:', result.data);
    return result.data;
  } catch (error) {
    console.error('[cleanupHelper] Error:', error);
    throw error;
  }
}

/**
 * Reclassify unclassified transactions using active rules
 */
export async function reclassifyTransactions(
  dryRun: boolean = true,
  onlyUnclassified: boolean = false
): Promise<ReclassifyResult> {
  console.log('[reclassifyHelper] Starting reclassification:', { dryRun, onlyUnclassified });

  const reclassifyFn = httpsCallable<{ dryRun: boolean; onlyUnclassified: boolean }, ReclassifyResult>(
    functions,
    'reclassifyTransactions'
  );

  try {
    const result = await reclassifyFn({ dryRun, onlyUnclassified });
    console.log('[reclassifyHelper] Result:', result.data);
    return result.data;
  } catch (error) {
    console.error('[reclassifyHelper] Error:', error);
    throw error;
  }
}

/**
 * Migrate merchant field (Issue #85)
 * Copy merchantNorm to merchant field for classification matching
 */
export async function migrateMerchantField(
  dryRun: boolean = true,
  confirm: boolean = false
): Promise<{
  success: boolean;
  message: string;
  transactionsFound: number;
  transactionsUpdated: number;
  dryRun: boolean;
}> {
  console.log('[cleanupHelper] Starting merchant field migration:', {
    dryRun,
    confirm,
  });

  const migrateFn = httpsCallable<
    { dryRun: boolean; confirm: boolean },
    {
      success: boolean;
      message: string;
      transactionsFound: number;
      transactionsUpdated: number;
      dryRun: boolean;
    }
  >(functions, 'migrateMerchantField');

  try {
    const result = await migrateFn({ dryRun, confirm });
    console.log('[cleanupHelper] Result:', result.data);
    return result.data;
  } catch (error) {
    console.error('[cleanupHelper] Error:', error);
    throw error;
  }
}

/**
 * Cleanup misclassified rules (Issue #85)
 * Deletes rules with wrong categoryId from bulk import bug
 */
export async function cleanupMisclassifiedRules(
  categoryId: string,
  dryRun: boolean = true,
  confirm: boolean = false
): Promise<{
  success: boolean;
  message: string;
  categoryId: string;
  rulesFound: number;
  rulesDeleted: number;
  dryRun: boolean;
}> {
  console.log('[cleanupHelper] Starting cleanup of misclassified rules:', {
    categoryId,
    dryRun,
    confirm,
  });

  const cleanupFn = httpsCallable<
    { categoryId: string; dryRun: boolean; confirm: boolean },
    {
      success: boolean;
      message: string;
      categoryId: string;
      rulesFound: number;
      rulesDeleted: number;
      dryRun: boolean;
    }
  >(functions, 'cleanupMisclassifiedRules');

  try {
    const result = await cleanupFn({ categoryId, dryRun, confirm });
    console.log('[cleanupHelper] Result:', result.data);
    return result.data;
  } catch (error) {
    console.error('[cleanupHelper] Error:', error);
    throw error;
  }
}

/**
 * Create Rules from Suggestions - Bulk rule creation (Issue #92)
 * Creates classification rules from selected suggestions
 */
export async function createRulesFromSuggestions(
  suggestions: Array<{
    merchant: string;
    categoryId: string;
  }>
): Promise<{
  timestamp: string;
  userId: string;
  created: number;
  failed: number;
  errors: Array<{ merchant: string; error: string }>;
  message: string;
}> {
  console.log(`[createRulesFromSuggestions] Creating ${suggestions.length} rules...`);

  const createFn = httpsCallable<
    {
      suggestions: Array<{
        merchant: string;
        categoryId: string;
      }>;
    },
    {
      timestamp: string;
      userId: string;
      created: number;
      failed: number;
      errors: Array<{ merchant: string; error: string }>;
      message: string;
    }
  >(functions, 'createRulesFromSuggestions');

  try {
    const result = await createFn({ suggestions });
    const data = result.data;

    console.log('📊 Rule Creation Results');
    console.log('=======================');
    console.log(`\n✅ Created: ${data.created}건`);
    console.log(`❌ Failed: ${data.failed}건`);

    if (data.errors.length > 0) {
      console.log('\n⚠️ Errors:');
      data.errors.forEach((error: { merchant: string; error: string }, index: number) => {
        console.log(`  ${index + 1}. [${error.merchant}] ${error.error}`);
      });
    }

    console.log(`\n${data.message}`);

    return data;
  } catch (error) {
    console.error('[createRulesFromSuggestions] Error:', error);
    throw error;
  }
}

/**
 * Migrate Internal Transfer Transactions (Issue #84)
 * Updates existing transactions matching internal transfer patterns to '내 계좌간 이동'
 */
export async function migrateInternalTransferTransactions(
  dryRun: boolean = true
): Promise<{
  success: boolean;
  message: string;
  totalTransactions: number;
  matchedTransactions: number;
  updatedTransactions: number;
  dryRun: boolean;
}> {
  console.log('[migrateInternalTransfer] Starting migration:', { dryRun });

  const migrateFn = httpsCallable<
    { dryRun: boolean },
    {
      success: boolean;
      message: string;
      totalTransactions: number;
      matchedTransactions: number;
      updatedTransactions: number;
      dryRun: boolean;
    }
  >(functions, 'migrateInternalTransferTransactions');

  try {
    const result = await migrateFn({ dryRun });
    const data = result.data;

    console.log('📊 Internal Transfer Migration Results');
    console.log('======================================');
    console.log(`\n🔍 Mode: ${data.dryRun ? 'DRY RUN' : 'EXECUTE'}`);
    console.log(`📊 Total Transactions: ${data.totalTransactions}`);
    console.log(`✅ Matched Transactions: ${data.matchedTransactions}`);
    console.log(`🔄 Updated Transactions: ${data.updatedTransactions}`);
    console.log(`\n${data.message}`);

    if (data.dryRun && data.matchedTransactions > 0) {
      console.log('\n💡 Next Step:');
      console.log('  Run with dryRun=false to apply changes:');
      console.log('  await window.migrateInternalTransferTransactions(false)');
    }

    return data;
  } catch (error) {
    console.error('[migrateInternalTransfer] Error:', error);
    throw error;
  }
}

/**
 * Backfill TransactionHash for Existing Transactions
 * Regenerates transactionHash for all existing transactions with updated formula
 * Formula: SHA256(date|time|merchantNorm|amount|currency|userId)
 */
export async function backfillTransactionHash(
  dryRun: boolean = true
): Promise<{
  success: boolean;
  mode: string;
  message: string;
  stats: {
    totalTransactions: number;
    updated: number;
    unchanged: number;
    errors: number;
    duration: number;
  };
}> {
  console.log('[backfillTransactionHash] Starting backfill:', { dryRun });

  const backfillFn = httpsCallable<
    { dryRun: boolean },
    {
      success: boolean;
      mode: string;
      message: string;
      stats: {
        totalTransactions: number;
        updated: number;
        unchanged: number;
        errors: number;
        duration: number;
      };
    }
  >(functions, 'backfillTransactionHash');

  try {
    const result = await backfillFn({ dryRun });
    const data = result.data;

    console.log('🔄 TransactionHash Backfill Results');
    console.log('====================================');
    console.log(`\n🔍 Mode: ${data.mode.toUpperCase()}`);
    console.log(`📊 Total Transactions: ${data.stats.totalTransactions}`);
    console.log(`✅ Updated: ${data.stats.updated}`);
    console.log(`⚪ Unchanged: ${data.stats.unchanged}`);
    console.log(`❌ Errors: ${data.stats.errors}`);
    console.log(`⏱️ Duration: ${(data.stats.duration / 1000).toFixed(2)}s`);
    console.log(`\n${data.message}`);

    if (dryRun && data.stats.updated > 0) {
      console.log('\n💡 Next Step:');
      console.log('  Run with dryRun=false to apply changes:');
      console.log('  await window.backfillTransactionHash(false)');
    }

    return data;
  } catch (error) {
    console.error('[backfillTransactionHash] Error:', error);
    throw error;
  }
}
